import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Button,
  Buttons,
  Container,
  Header,
  UserMenu,
  MinimizeButton,
} from "./styles";
import { useAuth } from "../../contexts/AuthContext";
import { useMenuPreferences } from "../../contexts/MenuPreferencesContext";
import usePlanAccess from "../../hooks/usePlanAccess";
import { DASHBOARD_TO_MODULE } from "../../config/modules";
import UserProfileModal from "../UserProfileModal";
import EditModeToggle from "./EditModeToggle";
import MenuItemDraggable from "./MenuItemDraggable";
import CategorySection from "./CategorySection";
import CreateCategoryModal from "./CreateCategoryModal";
import ConfirmModal from "../ConfirmModal";
import LogoFull from "../../imgs/logo.png";
import LogoIcon from "../../imgs/ico.png";
import { MdInventory } from "react-icons/md";
import { GoPersonFill, GoUpload, GoProject, GoDatabase } from "react-icons/go";
import { IoChevronBack, IoChevronForward, IoLogOutOutline } from "react-icons/io5";
import { BiTransfer } from "react-icons/bi";
import { FiTruck, FiShield, FiUsers, FiFileText, FiDownload, FiKey, FiDollarSign, FiTarget, FiMail, FiClipboard, FiActivity } from "react-icons/fi";
import { GoLaw, GoChecklist } from "react-icons/go";
import { DndContext, DragOverlay, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { isAutomecProject } from "../../config/automec";

// Mapeamento de ícones para cada dashboard
const DASHBOARD_ICONS = {
  'files': GoUpload,
  'transactions': BiTransfer,
  'notificacoes-fiscais': FiShield,
  'products': MdInventory,
  'clients': GoPersonFill,
  'suppliers': FiTruck,
  'reforma-tributaria': GoLaw,
  'validacao-fiscal': GoChecklist,
  'plcom': FiClipboard,
  'sped-reviewer': FiFileText,
  'downloads': FiDownload,
  'nfe-downloader': FiDownload,
  'lead-scraper': FiTarget,
  'certificates': FiKey,
  'email-marketing': FiMail,
  'xml-scan': FiFileText,
  'creditos': FiDollarSign,
  'admin': GoDatabase,
  'activity-logs': FiActivity,
  'projects': GoProject,
  'users': FiUsers,
  'automec-fornecedores': FiClipboard,
};

// Mapeamento de labels para cada dashboard
const DASHBOARD_LABELS = {
  'files': 'Arquivos',
  'transactions': 'Movimentações',
  'notificacoes-fiscais': 'Notifica\u00e7\u00f5es Fiscais',
  'products': 'Produtos',
  'clients': 'Clientes',
  'suppliers': 'Fornecedores',
  'reforma-tributaria': 'Reforma Tributária',
  'validacao-fiscal': 'Validação Fiscal',
  'plcom': 'PLCom',
  'sped-reviewer': 'Revisor SPED\'s',
  'downloads': 'Downloads',
  'nfe-downloader': 'NFe Downloader',
  'lead-scraper': 'Lead Scraper',
  'certificates': 'Certificados',
  'email-marketing': 'Email Marketing',
  'xml-scan': 'XML Scan',
  'creditos': 'Créditos',
  'admin': 'Admin',
  'activity-logs': 'Atividades',
  'projects': 'Projetos',
  'users': 'Usuários',
  'automec-fornecedores': 'Questionário Fornecedores',
};

export default function Menu({
  menuMinimized = false,
  activeDashboard = "files",
  onDashboardChange,
  onMenuToggle,
}) {
  const { user, logout, hasPermission, currentProject } = useAuth();
  const { canAccessDashboard } = usePlanAccess();
  const {
    preferences,
    editMode,
    toggleCategoryCollapse,
    toggleFavorite,
    toggleHidden,
    reorderItems,
    updatePreferences,
    addCategory,
    removeCategory,
    renameCategory,
    resetAllItemsToDefault
  } = useMenuPreferences();
  
  const [isElectron, setIsElectron] = useState(false);
  const [showLogout, setShowLogout] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [categoryModalMode, setCategoryModalMode] = useState('create');
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');
  const [globalEditMode, setGlobalEditMode] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [confirmDeleteCategory, setConfirmDeleteCategory] = useState(null);
  const menuHoverTimeoutRef = useRef(null);
  
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // Distância menor = mais sensível ao arrastar
      },
    })
  );

  useEffect(() => {
    setIsElectron(navigator.userAgent.toLowerCase().includes("electron"));
  }, []);

  // Handlers para hover global do menu - ativa apenas quando mouse fica PARADO por 3s
  const handleMenuMouseMove = () => {
    // Cancela o timeout anterior de ativação se houver movimento
    if (menuHoverTimeoutRef.current) {
      clearTimeout(menuHoverTimeoutRef.current);
    }
    
    // Cria novo timeout que só será executado se mouse ficar parado por 3s
    menuHoverTimeoutRef.current = setTimeout(() => {
      setGlobalEditMode(true);
    }, 3000);
  };

  const handleMenuMouseLeave = () => {
    // Cancela o timeout de ativação se mouse saiu antes de ativar
    if (menuHoverTimeoutRef.current) {
      clearTimeout(menuHoverTimeoutRef.current);
      menuHoverTimeoutRef.current = null;
    }
    
    // Desativa imediatamente ao sair do menu
    setGlobalEditMode(false);
  };

  useEffect(() => {
    return () => {
      if (menuHoverTimeoutRef.current) {
        clearTimeout(menuHoverTimeoutRef.current);
      }
    };
  }, []);

  const getUserInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  };

  const handleLogout = () => {
    setConfirmLogout(true);
  };

  // Organizar itens por categoria com ordenação
  const itemsByCategory = useMemo(() => {
    const result = {};
    
    preferences.categories.forEach(cat => {
      result[cat.id] = preferences.items
        .filter(item => item.categoryId === cat.id)
        .sort((a, b) => a.order - b.order);
    });
    
    return result;
  }, [preferences]);

  // Categorias ordenadas
  const sortedCategories = useMemo(() => {
    return [...preferences.categories].sort((a, b) => a.order - b.order);
  }, [preferences.categories]);

  // Handlers de drag & drop
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
    // Ativa modo de edição automaticamente ao começar a arrastar
    setGlobalEditMode(true);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (!over) {
      setActiveId(null);
      return;
    }

    const activeItem = preferences.items.find(i => i.id === active.id);
    const overItem = preferences.items.find(i => i.id === over.id);

    if (!activeItem) {
      setActiveId(null);
      return;
    }

    // Se soltou sobre outro item da mesma categoria, reordena
    if (overItem && activeItem.categoryId === overItem.categoryId) {
      const categoryItems = itemsByCategory[activeItem.categoryId];
      const oldIndex = categoryItems.findIndex(i => i.id === active.id);
      const newIndex = categoryItems.findIndex(i => i.id === over.id);

      if (oldIndex !== newIndex) {
        const reorderedIds = arrayMove(
          categoryItems.map(i => i.id),
          oldIndex,
          newIndex
        );
        reorderItems(activeItem.categoryId, reorderedIds);
      }
    }
    // Se soltou sobre uma categoria diferente, move o item
    else if (preferences.categories.find(c => c.id === over.id)) {
      const newCategoryId = over.id;
      if (newCategoryId !== activeItem.categoryId) {
        const newPreferences = {
          ...preferences,
          items: preferences.items.map(item =>
            item.id === active.id
              ? { ...item, categoryId: newCategoryId, order: 0 }
              : item
          )
        };
        updatePreferences(newPreferences);
      }
    }

    setActiveId(null);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  // Handlers de modal de categoria
  const handleCreateCategory = () => {
    setCategoryModalMode('create');
    setEditingCategoryId(null);
    setEditingCategoryName('');
    setCategoryModalOpen(true);
  };

  const handleEditCategory = (categoryId, categoryName) => {
    setCategoryModalMode('edit');
    setEditingCategoryId(categoryId);
    setEditingCategoryName(categoryName);
    setCategoryModalOpen(true);
  };

  const handleSaveCategory = (name) => {
    if (categoryModalMode === 'create') {
      addCategory(name);
    } else if (categoryModalMode === 'edit' && editingCategoryId) {
      renameCategory(editingCategoryId, name);
    }
  };

  const handleDeleteCategory = (categoryId) => {
    setConfirmDeleteCategory(categoryId);
  };

  // Renderizar um item de menu
  const renderMenuItem = (item) => {
    const Icon = DASHBOARD_ICONS[item.id];
    const label = DASHBOARD_LABELS[item.id];

    if (!Icon || !label) return null;

    // Telas exclusivas de projetos específicos (ex: questionário Automec só
    // aparece quando o projeto AUTOMEC COMERCIAL está selecionado)
    if (item.id === 'automec-fornecedores' && !isAutomecProject(currentProject)) return null;

    // Acesso definido pelo plano (trial = tudo, exceto telas god)
    if (!canAccessDashboard(item.id)) return null;

    // Acesso definido pelas permissões granulares do usuário/projeto
    // (respeitado também durante simulação de usuário — ver AuthContext.hasPermission).
    const moduleId = DASHBOARD_TO_MODULE[item.id];
    if (moduleId && !hasPermission(moduleId)) return null;

    return (
      <MenuItemDraggable
        key={item.id}
        id={item.id}
        editMode={editMode}
        globalEditMode={globalEditMode}
        isFavorite={item.isFavorite}
        isHidden={item.isHidden}
        onToggleFavorite={() => toggleFavorite(item.id)}
        onToggleHidden={() => toggleHidden(item.id)}
      >
        <Button
          $menuMinimized={menuMinimized}
          $active={activeDashboard === item.id}
          $dashboard={item.id}
          $isHidden={item.isHidden}
          onClick={() => onDashboardChange && onDashboardChange(item.id)}
        >
          <Icon />
          <p>{label}</p>
        </Button>
      </MenuItemDraggable>
    );
  };

  return (
    <Container 
      data-tour="menu"
      $menuMinimized={menuMinimized}
      $editMode={editMode || globalEditMode}
      onMouseMove={handleMenuMouseMove}
      onMouseLeave={handleMenuMouseLeave}
    >
      <Header $menuMinimized={menuMinimized}>
        {menuMinimized ? (
          <img src={LogoIcon} alt="Vidal" className="logo-icon" />
        ) : (
          <img src={LogoFull} alt="Vidal" className="logo-full" />
        )}
        {!isElectron && onMenuToggle && !menuMinimized && (
          <MinimizeButton onClick={onMenuToggle} $menuMinimized={menuMinimized}>
            <IoChevronBack />
          </MinimizeButton>
        )}
      </Header>
      {!isElectron && onMenuToggle && menuMinimized && (
        <MinimizeButton
          onClick={onMenuToggle}
          $menuMinimized={menuMinimized}
          $isBelowIcon={true}
        >
          <IoChevronForward />
        </MinimizeButton>
      )}
      
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <Buttons>
          {sortedCategories.map(category => {
            const categoryItems = itemsByCategory[category.id] || [];
            
            // Ocultar categorias "Favoritos" e "Ocultos" se estiverem vazias
            if ((category.id === 'favorites' || category.id === 'hidden') && categoryItems.length === 0) {
              return null;
            }
            
            return (
              <CategorySection
                key={category.id}
                category={category}
                items={categoryItems}
                editMode={editMode}
                menuMinimized={menuMinimized}
                onToggleCollapse={() => toggleCategoryCollapse(category.id)}
                onRename={!category.isSystem ? () => handleEditCategory(category.id, category.name) : undefined}
                onDelete={!category.isSystem ? () => handleDeleteCategory(category.id) : undefined}
                onReset={category.id === 'dashboards' ? resetAllItemsToDefault : undefined}
              >
                {categoryItems.map(item => renderMenuItem(item))}
              </CategorySection>
            );
          })}
        </Buttons>
        
        <DragOverlay>
          {activeId ? (
            <div style={{ opacity: 0.8 }}>
              {renderMenuItem(preferences.items.find(i => i.id === activeId))}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      <UserMenu 
        data-tour="user-menu"
        $menuMinimized={menuMinimized}
        onMouseEnter={() => setShowLogout(true)}
        onMouseLeave={() => setShowLogout(false)}
      >
        <div className="user" onClick={() => setIsProfileModalOpen(true)}>
          <div className="thumb">
            <div className="circle">
              <p>{getUserInitials(user?.name || "Usuário")}</p>
            </div>
          </div>
          {!menuMinimized && (
            <div className="text">
              <p className="user-name">{user?.name || "Usuário"}</p>
              <p className="user-email">{user?.email || "email@exemplo.com"}</p>
            </div>
          )}
        </div>
        {!menuMinimized && showLogout && (
          <button className="logout-btn" onClick={handleLogout} title="Sair">
            <IoLogOutOutline />
            <span>Sair</span>
          </button>
        )}
      </UserMenu>

      <UserProfileModal 
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
      />

      <CreateCategoryModal
        isOpen={categoryModalOpen}
        onClose={() => setCategoryModalOpen(false)}
        onSave={handleSaveCategory}
        initialValue={editingCategoryName}
        mode={categoryModalMode}
      />

      <ConfirmModal
        isOpen={confirmLogout}
        onClose={() => setConfirmLogout(false)}
        onConfirm={() => { setConfirmLogout(false); logout(); }}
        title="Sair da conta"
        message="Deseja realmente sair da sua conta?"
        variant="warning"
        confirmText="Sair"
        cancelText="Cancelar"
        confirmVariant="danger"
      />

      <ConfirmModal
        isOpen={!!confirmDeleteCategory}
        onClose={() => setConfirmDeleteCategory(null)}
        onConfirm={() => { removeCategory(confirmDeleteCategory); setConfirmDeleteCategory(null); }}
        title="Deletar categoria"
        message="Tem certeza que deseja deletar esta categoria? Os itens serão movidos para a categoria padrão."
        variant="danger"
        confirmText="Deletar"
        cancelText="Cancelar"
        confirmVariant="danger"
      />
    </Container>
  );
}
